import { FormControl, NG_ASYNC_VALIDATORS } from '@angular/forms';
import { Directive, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { finalize, map, Observable, of, Subscription } from 'rxjs';

import { UsaWrestlingMembership } from '@reg/shared/model';
import { UsaWrestlingService } from '@reg/shared/service';

@Directive({
    selector: '[floAsyncUsaWrestlingNumberValidator]',
    providers: [
        { provide: NG_ASYNC_VALIDATORS, useExisting: UsaWrestlingNumberDirective, multi: true },
    ],
})
export class UsaWrestlingNumberDirective implements OnDestroy {
    @Output() public isValidatingNumberChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public membershipChange: EventEmitter<UsaWrestlingMembership> =
        new EventEmitter<UsaWrestlingMembership>();

    @Input() public eventId: string;
    @Input() public coachCertificationLevel: string;
    @Input() public isCoachValidation: boolean = false;

    private _isValidating: boolean = false;
    @HostBinding('class.is-validating')
    set isValidating(isValidating: boolean) {
        this._isValidating = isValidating;
        this.isValidatingNumberChange.emit(isValidating);
    }

    get isValidating(): boolean {
        return this._isValidating;
    }

    private formControl: FormControl;
    private _divisionId: string;
    private timeout: any;
    private defaultDelay: number = 600; // wait 600 milliseconds before validating so we do not validate on every key stroke
    private delay: number = this.defaultDelay;
    private valueChangedSubscription: Subscription;

    @Input()
    set divisionId(value: string) {
        if (this.formControl &&
            this._divisionId !== value) {
            // If the value is changing force a validation check
            this._divisionId = value;
            this.delay = 0; // no need to wait when the division changes
            this.formControl.updateValueAndValidity();
        } else {
            this._divisionId = value;
        }
    }

    get divisionId(): string {
        return this._divisionId;
    }

    constructor(private usaWrestlingService: UsaWrestlingService) {
    }

    public validate(control: FormControl): Observable<any> {
        clearTimeout(this.timeout);
        this.isValidating = true;

        if (!this.formControl) {
            this.formControl = control;

            // First time loading so lets validate immediately
            this.delay = 0;

            this.valueChangedSubscription = this.formControl.valueChanges.subscribe(() => {
                // If user selects the whole value and hits backsapce
                // the validate method isn't called so we workaround
                // that by subscribing to valueChanges
                if (!this.formControl.value) {
                    this.membershipChange.emit(null);
                    this.isValidating = false;
                    return of(null);
                }
            });
        }

        if (!control.value) {
            this.membershipChange.emit(null);
            this.isValidating = false;
            return of(null);
        }

        // Set a timeout so we don't make an http call for every key stroke.
        return new Observable((obs: any): void => {
            this.timeout = setTimeout(
                () => {
                    this.delay = this.defaultDelay;

                    this.getMembership().pipe(
                        map((membership: UsaWrestlingMembership) => {
                            if (!membership || !control.value) {
                                this.membershipChange.emit(null);
                                return null;
                            }

                            this.membershipChange.emit(membership);
                            if (membership.isValid) {
                                return null;
                            }
                            return { invalidUsaNum: true };
                        }),
                        finalize(() => {
                            obs.complete();
                            this.isValidating = false;
                        }),
                    ).subscribe({
                        next: (data: any) => { obs.next(data); },
                        error: (error: any) => { obs.next({ invalidUsaNum: true }); },
                    });
                },
                this.delay,
            );
        });
    }

    public ngOnDestroy(): void {
        if (this.valueChangedSubscription) {
            this.valueChangedSubscription.unsubscribe();
        }
    }

    private getMembership(): Observable<UsaWrestlingMembership> {
        if (this.isCoachValidation) {
            return this.usaWrestlingService
                .getCoachMembership(this.eventId, this.formControl.value, this.coachCertificationLevel);
        }
        return this.usaWrestlingService.getAthleteMembership(this.eventId, this.formControl.value, this.divisionId);
    }
}
